// UserFeedGridList.jsx
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Loading from '../component/Loading.jsx';
import {
  InfiniteScroller,
  ScrollItem,
} from '../component/InfiniteScroller.jsx';
import media from '../style/media.js';
import UserHashtagCard from '../container/UserHashtagCard.js';
import { headerHeight } from '../style/variables.js';
import BackIconSource from '../../img/ic_topbar_back.svg';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

export const UserFeedGridList = ({
  userIds,
  fetchFeeds,
  category,
  nextPage,
  isNextPageFetching,
  isAuthed,
  isLeaderboard,
  goBack,
}) => {
  useEffect(() => {
    if (!category) return;

    const nextTick = setTimeout(() => {
      fetchFeeds({ type: category, page: 1 });
    }, 0);

    return () => {
      clearTimeout(nextTick);
    };
  }, [category, fetchFeeds, isAuthed]);

  const { t } = useTranslation();

  return (
    <StyledUserHashtagDetailList>
      <Header>
        <BackButton onClick={goBack}>
          <img src={BackIconSource} alt="" height={32} width={32} />
        </BackButton>
        <HeaderText>
          {t(category, { ns: TranslationNamespace.FEED })}
        </HeaderText>
      </Header>
      <InfiniteScroller
        pageStart={1}
        hasMore={nextPage !== null}
        loadMore={() => {
          nextPage
            ? fetchFeeds({
                type: category,
                page: nextPage,
              })
            : null;
        }}
        isFetching={isNextPageFetching}
        threshold={100}
      >
        <UserHashtagDetailItemWrapper>
          <ScrollItem
            loader={
              <ItemWrapper>
                <LoadingWrapper>
                  <Loading />
                </LoadingWrapper>
              </ItemWrapper>
            }
          >
            {userIds.map((id, index) => {
              return (
                <UserHashtagCard
                  key={id}
                  userId={id}
                  shouldShowRank={isLeaderboard}
                  rank={isLeaderboard ? index + 1 : undefined}
                />
              );
            })}
          </ScrollItem>
        </UserHashtagDetailItemWrapper>
      </InfiniteScroller>
    </StyledUserHashtagDetailList>
  );
};

UserFeedGridList.propTypes = {
  userIds: PropTypes.array,
  isNextPageFetching: PropTypes.bool,
  isAuthed: PropTypes.bool,
  isLeaderboard: PropTypes.bool,
  category: PropTypes.string,
  nextPage: PropTypes.number,
  fetchFeeds: PropTypes.func,
  goBack: PropTypes.func,
};

UserFeedGridList.defaultProps = {
  userIds: [],
  isNextPageFetching: false,
  isAuthed: false,
  isLeaderboard: false,
  category: null,
  nextPage: null,
  goBack: () => null,
  fetchFeeds: () => null,
};

const StyledUserHashtagDetailList = styled.div`
  max-width: 682px;
  padding-top: ${headerHeight + 20}px;
  margin: auto;
  border-right: 1px solid rgba(71, 71, 71, 0.4);
  border-left: 1px solid rgba(71, 71, 71, 0.4);
  ${media.mobile`
    padding-top: 0px;
    border-right: none;
    border-left: none;
  `}
`;

const UserHashtagDetailItemWrapper = styled.div`
  margin: 0;
  padding: 0 16px;
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  ${media.mobile`
    padding: 20px 16px 0;
    gap: 8px;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  `}
`;

const Header = styled.header`
  padding: 8px 16px 20px 16px;
  ${media.mobile`
    padding: 16px;
    border-bottom: 1px solid rgba(71, 71, 71, 0.40);
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: #191919;
    display: flex;
    align-items: center;
  `}
`;

const HeaderText = styled.h1`
  font-size: 20px;
  font-weight: 600;
  line-height: 150%;
  margin: 0;
  padding: 0;
  ${media.mobile`
    font-size: 16px;
  `}
`;

const BackButton = styled.button`
  ${ResetButtonStyle}
  display: none;
  width: 32px;
  height: 32px;
  ${media.mobile`
    display: block;
    margin-right: 8px;
  `}
`;

const ItemWrapper = styled.div`
  position: relative;
  padding-top: 100%;
`;

const LoadingWrapper = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default UserFeedGridList;
